import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Group, Loader, Modal, Paper, Select, Stack, Table, Tabs, Text, Textarea, TextInput, Title } from "@mantine/core";

import { useAccessQuery, useSaveAccess } from "@hooks/queries/accountManagement/useAccountManagement";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { PermissionCatalogEntry, RolePermissionPolicy, UserPermissionPolicy } from "@customTypes/accountManagement";


type Grants = Record<string, "assigned" | "all" | "deny">;
interface ChangeEvent { actor_id: string; reason: string; timestamp: string; revision: number; before: Grants; after: Grants }

export const AccessManagement = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string | null>("roles");
  const [role, setRole] = useState<string | null>("receptionist");
  const [userId, setUserId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<Grants>({});
  const [reason, setReason] = useState("");
  const [review, setReview] = useState(false);
  const [error, setError] = useState("");
  const catalog = useAccessQuery<PermissionCatalogEntry[]>("catalog");
  const policies = useAccessQuery<RolePermissionPolicy[]>("roles");
  const users = useGetUsers({ role: "doctor,nurse,receptionist" });
  const person = useAccessQuery<UserPermissionPolicy>(`users/${userId}`, Boolean(userId));
  const save = useSaveAccess();
  const individual = tab === "people";
  const target = individual ? userId : role;
  const path = individual ? `users/${userId}` : `roles/${role}`;
  const policy = policies.data?.find(item => item.role === role);
  const current = individual ? person.data?.overrides : policy?.grants;
  const revision = individual ? person.data?.revision : policy?.revision;
  const history = useAccessQuery<ChangeEvent[]>(`history/${individual ? "user" : "role"}/${target}`, Boolean(target));
  const editableUsers = users.data?.filter(user => !user.roles.some(value => ["admin", "patient", "recruiter"].includes(value))) || [];

  useEffect(() => {
    setDraft(current || {});
    setReason("");
    setError("");
    setReview(false);
  }, [target, individual, revision, current]);

  const changes = useMemo(() => Array.from(new Set([...Object.keys(current || {}), ...Object.keys(draft)]))
    .filter(key => current?.[key] !== draft[key]), [current, draft]);
  const scopeLabel = (scope?: string, key?: string) => {
    if (scope === "all" && catalog.data?.find(entry => entry.key === key)?.scopes.length === 1) return t("access.allow");
    const area = key?.split(".")[0];
    if (scope && ["assigned", "all"].includes(scope) && ["appointments", "queue", "billing", "reports"].includes(area || "")) {
      return t(`access.scopes.${area}.${scope}`);
    }
    return t(`access.${scope || (individual ? "inherit" : "deny")}`);
  };
  const permissionLabel = (entry: PermissionCatalogEntry) => `${t(`access.areas.${entry.area}`)} — ${t(`access.actions.${entry.action}`)}`;
  const entries = catalog.data?.filter(entry => !entry.protected && permissionLabel(entry).toLowerCase().includes(search.toLowerCase())) || [];
  const baseline = useMemo(() => {
    const result: Grants = {};
    if (individual) for (const policy of policies.data || []) {
      if (person.data?.roles.includes(policy.role)) for (const [key, scope] of Object.entries(policy.grants)) {
        if (result[key] !== "all") result[key] = scope;
      }
    }
    return result;
  }, [individual, policies.data, person.data]);
  const inheritedRoles = (key: string) => (policies.data || [])
    .filter(policy => person.data?.roles.includes(policy.role) && policy.grants[key])
    .map(policy => t(policy.role)).join(", ");
  const effective = (key: string) => individual ? (draft[key] === "deny" ? undefined : draft[key] || baseline[key]) : draft[key];

  if (catalog.isLoading || policies.isLoading) return <Loader />;
  if (catalog.isError || policies.isError || users.isError) return <Alert color="red">{t("access.loadError")}</Alert>;

  return <Stack gap="lg" pb="calc(var(--footer-height, 42px) + 2rem)">
    <div><Title order={3}>{t("access.title")}</Title><Text c="dimmed" size="sm">{t("access.description")}</Text></div>
    <Alert>{t("access.adminProtected")}</Alert>
    <Tabs value={tab} onChange={setTab}>
      <Tabs.List><Tabs.Tab value="roles">{t("access.roles")}</Tabs.Tab><Tabs.Tab value="people">{t("access.people")}</Tabs.Tab></Tabs.List>
    </Tabs>
    <Group align="end">
      {individual ? <Select label={t("access.person")} searchable value={userId} onChange={setUserId}
        data={editableUsers.map(user => ({ value: user.id, label: user.fullName || user.email || user.username }))} />
        : <Select label={t("role")} value={role} onChange={setRole} allowDeselect={false}
          data={["doctor", "nurse", "receptionist"].map(value => ({ value, label: t(value) }))} />}
      <TextInput label={t("access.search")} value={search} onChange={event => setSearch(event.currentTarget.value)} />
      {!individual && <Text size="sm" c="dimmed">{t("access.affected", { count: users.data?.filter(user => user.roles.includes(role || "")).length || 0 })}</Text>}
    </Group>
    {individual && person.isError && <Alert color="red">{t("access.loadError")}</Alert>}
    {target && current && <>
      <Paper withBorder p="md">
        <Table.ScrollContainer minWidth={620}><Table>
          <Table.Thead><Table.Tr><Table.Th>{t("access.permission")}</Table.Th><Table.Th>{t("access.setting")}</Table.Th><Table.Th>{t("access.effective")}</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>{entries.map((entry, index) => <Fragment key={entry.key}>
            {(index === 0 || entries[index - 1].area !== entry.area) && <Table.Tr><Table.Td colSpan={3}><Text fw={700}>{t(`access.areas.${entry.area}`)}</Text></Table.Td></Table.Tr>}
            <Table.Tr>
            <Table.Td><Text size="sm" fw={500}>{permissionLabel(entry)}</Text></Table.Td>
            <Table.Td><Select aria-label={permissionLabel(entry)} value={draft[entry.key] || (individual ? "inherit" : "deny")}
              allowDeselect={false} data={[
                ...(individual ? [{ value: "inherit", label: t("access.inherit") }] : []),
                { value: "deny", label: t("access.deny") },
                ...entry.scopes.map(value => ({ value, label: scopeLabel(value, entry.key) })),
              ]} onChange={value => setDraft(previous => {
                const next = { ...previous };
                if (!value || value === "inherit" || (!individual && value === "deny")) delete next[entry.key];
                else next[entry.key] = value as Grants[string];
                return next;
              })} /></Table.Td>
            <Table.Td><Badge color={effective(entry.key) ? "teal" : "gray"}>{effective(entry.key) ? scopeLabel(effective(entry.key), entry.key) : t("access.deny")}</Badge>
              {individual && <Text size="xs" c="dimmed">{draft[entry.key] ? t("access.individual") : inheritedRoles(entry.key) ? t("access.fromRoles", { roles: inheritedRoles(entry.key) }) : t("access.noRoleGrant")}</Text>}
            </Table.Td>
          </Table.Tr></Fragment>)}</Table.Tbody>
        </Table></Table.ScrollContainer>
      </Paper>
      <Textarea label={t("access.reason")} required value={reason} maxLength={1000} onChange={event => setReason(event.currentTarget.value)} />
      {error && <Alert color="red">{error}<Button variant="subtle" onClick={() => {
        void policies.refetch(); if (individual) void person.refetch(); void history.refetch();
      }}>{t("access.reload")}</Button></Alert>}
      <Group justify="end"><Button variant="default" disabled={!changes.length} onClick={() => setDraft(current || {})}>{t("access.discard")}</Button>
        <Button disabled={!changes.length || !reason.trim()} onClick={() => setReview(true)}>{t("access.review", { count: changes.length })}</Button></Group>
      <Title order={4}>{t("access.history")}</Title>
      {history.isLoading ? <Loader size="sm" /> : history.isError ? <Alert color="red">{t("access.loadError")}</Alert> : !history.data?.length ? <Text c="dimmed">{t("access.noHistory")}</Text> : history.data.map(event => <Paper withBorder p="sm" key={event.revision}>
        <Group justify="space-between"><Text fw={500}>{event.reason}</Text><Text size="xs">{new Date(event.timestamp).toLocaleString()}</Text></Group>
        <Text size="xs" c="dimmed">{t("access.changedBy", { actor: users.data?.find(user => user.id === event.actor_id)?.fullName || event.actor_id, revision: event.revision })}</Text>
        {Array.from(new Set([...Object.keys(event.before), ...Object.keys(event.after)])).filter(key => event.before[key] !== event.after[key]).map(key => <Text size="xs" key={key}>
          {catalog.data?.find(entry => entry.key === key) ? permissionLabel(catalog.data.find(entry => entry.key === key)!) : key}: {scopeLabel(event.before[key], key)} → {scopeLabel(event.after[key], key)}
        </Text>)}
        <Button variant="subtle" size="xs" onClick={() => { setDraft(event.after); setReason(""); }}>{t("access.restore", { revision: event.revision })}</Button>
      </Paper>)}
    </>}
    <Modal opened={review} onClose={() => !save.isLoading && setReview(false)} title={t("access.reviewTitle")} size="lg">
      <Stack><Text fw={600}>{individual ? editableUsers.find(user => user.id === userId)?.fullName : t(role || "")}</Text>{changes.map(key => <Text key={key}>{(catalog.data?.find(entry => entry.key === key) ? permissionLabel(catalog.data.find(entry => entry.key === key)!) : key)}: {scopeLabel(current?.[key], key)} → {scopeLabel(draft[key], key)}</Text>)}
        <Text size="sm">{reason}</Text><Button loading={save.isLoading} onClick={() => save.mutate({ path, grants: draft, revision: revision!, reason: reason.trim() }, {
          onSuccess: () => setReview(false),
          onError: (failure: any) => { setReview(false); setError(t(failure.response?.status === 409 ? "access.conflict" : "access.saveError")); },
        })}>{t("access.save")}</Button>
      </Stack>
    </Modal>
  </Stack>;
};
